// pages/payments/PaymentList.jsx
// List-first view of karigar payments (money paid OUT). "Add New" opens the form modal.
import { useEffect, useMemo, useState } from "react";
import { paymentsAPI } from "../../services/api";
import { usePagination } from "../../hooks/usePagination";
import { Loading, Pagination, EmptyState } from "../../components/ui";
import { fmt } from "../../utils/helpers";
import PaymentFormModal from "./PaymentFormModal";

const MODE_BADGE = {
  Cash:   "badge-green",
  UPI:    "badge-blue",
  Card:   "badge-purple",
  Cheque: "badge-orange",
};

export default function PaymentList() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");
  const [search, setSearch]     = useState("");
  const [showForm, setShowForm] = useState(false);

  // Date range is filtered SERVER-side, so changing it refetches. Search stays client
  // side — it's instant and the endpoint has no name filter.
  const [from, setFrom] = useState("");
  const [to, setTo]     = useState("");

  const load = () => {
    setLoading(true);
    setError("");
    paymentsAPI
      .list({ fromDate: from, toDate: to })
      .then((d) => setPayments(Array.isArray(d) ? d : []))
      .catch((e) => {
        console.error("[PaymentList] load error:", e);
        setPayments([]);
        setError(e?.message || "The payments service isn't available right now.");
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, [from, to]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return payments;
    return payments.filter(
      (p) =>
        (p.karigarName ?? "").toLowerCase().includes(q) ||
        String(p.id ?? "").toLowerCase().includes(q)
    );
  }, [payments, search]);

  const { paged, page, setPage, total, count } = usePagination(filtered, 8);

  const totalPaid  = payments.reduce((s, p) => s + (p.amount || 0), 0);
  const totalKasar = payments.reduce((s, p) => s + (p.kasar  || 0), 0);

  const rangeLabel =
    from && to ? `${fmt(from)} → ${fmt(to)}`
    : from     ? `From ${fmt(from)}`
    : to       ? `Until ${fmt(to)}`
    : "All time";

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Payments</h1>
          <p className="page-sub">
            {payments.length} payment{payments.length !== 1 ? "s" : ""} · ₹{totalPaid.toLocaleString()} paid to karigars
            {totalKasar > 0 ? ` · ₹${totalKasar.toLocaleString()} kasar` : ""} · {rangeLabel}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          ＋ Add New
        </button>
      </div>

      {/* Date range — server-side filter */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 20, flexWrap: "wrap" }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">From</label>
          <input type="date" className="form-input" value={from} max={to || undefined}
                 onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">To</label>
          <input type="date" className="form-input" value={to} min={from || undefined}
                 onChange={(e) => setTo(e.target.value)} />
        </div>
        {(from || to) && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setFrom(""); setTo(""); }}>
            ✕ Clear dates
          </button>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <div className="search-bar" style={{ flex: 1, maxWidth: 340 }}>
            <span className="search-icon">🔍</span>
            <input
              className="form-input"
              placeholder="Search karigar or payment ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {search && <button className="btn btn-ghost btn-sm" onClick={() => setSearch("")}>✕ Clear</button>}
        </div>

        {loading ? (
          <Loading />
        ) : error ? (
          <EmptyState
            icon="⚠️"
            title="Couldn't load payments"
            subtitle={error}
            action={<button className="btn btn-secondary btn-sm" onClick={load}>Retry</button>}
          />
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Payment ID</th>
                    <th>Karigar</th>
                    <th>Amount</th>
                    <th>Kasar</th>
                    <th>Mode</th>
                    <th>Date</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.length === 0 ? (
                    <tr>
                      <td colSpan={7}>
                        <EmptyState
                          icon="💳"
                          title={
                            search      ? "No payments match your search"
                            : from || to ? "No payments in this date range"
                            : "No payments yet"
                          }
                          subtitle={search || from || to ? undefined : "Click “Add New” to record your first karigar payment."}
                        />
                      </td>
                    </tr>
                  ) : (
                    paged.map((p, i) => (
                      <tr key={p.id ?? `${p.karigarId}-${i}`}>
                        <td className="td-code">{p.id ?? "—"}</td>
                        <td><strong>{p.karigarName || "—"}</strong></td>
                        <td style={{ color: "var(--gold)", fontWeight: 600 }}>₹{(p.amount || 0).toLocaleString()}</td>
                        <td className="td-muted">{p.kasar ? `₹${p.kasar.toLocaleString()}` : "—"}</td>
                        <td>
                          <span className={`badge ${MODE_BADGE[p.mode] || "badge-blue"}`}>
                            <span className="badge-dot" />{p.mode || "—"}
                          </span>
                        </td>
                        <td className="td-muted">{fmt(p.date)}</td>
                        <td className="td-muted">{p.remarks || "—"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <Pagination page={page} total={total} setPage={setPage} count={count} perPage={8} />
          </>
        )}
      </div>

      {showForm && (
        <PaymentFormModal
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}
